import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { Popover } from 'antd';
import type { PopoverProps } from 'antd';
import { capitalCase } from 'change-case';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useWorkspaceResourceWebsite } from '@/hooks/useWorkspaceResourceWebsite';
import type { InboxItem } from '@/services/database';
import { getResourceActivityActionLabel } from '@/shared/components/activity/resourceActivityDescription';
import { WorkspaceResourceAvatar } from '@/shared/components/workspace-resources';
import { ResourceActivityEventType } from '@/types';
import type { ResourceActivityResourceType } from '@/types';
import { ellipsisStyling } from '@/utilities';

import { workspaceResourcePreviewPlacement } from './registry';
import type { WorkspaceResourceHoverCardData } from './types';
import WorkspaceResourcePreviewContent from './WorkspaceResourcePreviewContent';

import './index.css';

export interface WorkspaceResourceReference {
    createdAt?: Date | string;
    deletedAt?: Date | string;
    description?: string;
    href?: string;
    id: string;
    label: string;
    listType?: string;
    resourceType: ResourceActivityResourceType;
    updatedAt?: Date | string;
    website?: string;
}

type EnrichedWorkspaceResourcePopoverProps =
    | {
          children?: ReactNode;
          item: InboxItem;
          placement?: PopoverProps['placement'];
          publicMemoId?: string;
          publicWorkspaceMetadata?: boolean;
          reference?: never;
          zIndex?: PopoverProps['zIndex'];
      }
    | {
          children?: ReactNode;
          item?: never;
          placement?: PopoverProps['placement'];
          publicMemoId?: string;
          publicWorkspaceMetadata?: boolean;
          reference: WorkspaceResourceReference;
          zIndex?: PopoverProps['zIndex'];
      };

const resourceLabel = (resourceType: ResourceActivityResourceType) => capitalCase(resourceType.replace(/s$/, ''));

const metadataString = (metadata: Record<string, unknown> | undefined, keys: string[]) => {
    for (const key of keys) {
        const value = metadata?.[key];

        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    return undefined;
};

const metadataDate = (metadata: Record<string, unknown> | undefined, key: string) => {
    const value = metadata?.[key];

    return value instanceof Date || typeof value === 'string' ? value : undefined;
};

interface ResourcePopoverProps {
    children: ReactNode;
    data: WorkspaceResourceHoverCardData;
    placement: PopoverProps['placement'];
    publicMemoId?: string;
    zIndex?: PopoverProps['zIndex'];
}

const WorkspaceResourcePopover: FC<ResourcePopoverProps> = ({ children, data, placement, publicMemoId, zIndex }) => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(false);

    return (
        <Popover
            arrow={false}
            classNames={{
                body: 'enriched-workspace-resource-popover__body',
                root: 'enriched-workspace-resource-popover',
            }}
            content={
                <WorkspaceResourcePreviewContent
                    accessMode={publicMemoId ? 'public' : 'authenticated'}
                    actionsVisible={!publicMemoId}
                    data={data}
                    onRequestClose={() => setOpen(false)}
                    open={open}
                    publicMemoId={publicMemoId}
                />
            }
            mouseEnterDelay={0.15}
            mouseLeaveDelay={0.1}
            onOpenChange={setOpen}
            open={open}
            placement={placement}
            styles={{
                body: { background: 'transparent', border: 0, boxShadow: 'none', padding: 0 },
                root: { filter: 'none', padding: 0 },
            }}
            zIndex={zIndex}
        >
            <span
                style={{
                    cursor: data.href ? 'pointer' : 'help',
                    display: 'inline-flex',
                    maxWidth: '100%',
                    outlineColor: token.colorPrimary,
                }}
            >
                {children}
            </span>
        </Popover>
    );
};

const EnrichedWorkspaceResourcePopover: FC<EnrichedWorkspaceResourcePopoverProps> = (props): JSX.Element => {
    const { token } = useAntDesignTheme();
    const itemMetadata = props.item?.activity.metadata ?? {};
    const publicReference = Boolean(props.publicMemoId && props.reference);
    const website = useWorkspaceResourceWebsite(
        props.reference?.resourceType ?? props.item?.resourceType,
        props.reference?.id ?? props.item?.resourceId,
        publicReference
            ? undefined
            : (props.reference?.website ?? metadataString(itemMetadata, ['website', 'companyWebsite'])),
    );

    if (props.reference) {
        const { reference } = props;
        const trigger = props.children ?? (
            <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem', maxWidth: '100%' }}>
                <WorkspaceResourceAvatar
                    listType={reference.listType}
                    resource={reference.resourceType}
                    size={20}
                    website={website}
                />
                <span style={ellipsisStyling}>{reference.label}</span>
            </span>
        );

        if (props.publicMemoId && !props.publicWorkspaceMetadata) {
            return <>{trigger}</>;
        }

        return (
            <WorkspaceResourcePopover
                data={{
                    createdAt: props.publicMemoId ? undefined : reference.createdAt,
                    deletedAt: props.publicMemoId ? undefined : reference.deletedAt,
                    href: reference.href,
                    id: reference.id,
                    label: reference.label,
                    listType: reference.listType,
                    resourceType: reference.resourceType,
                    summary: props.publicMemoId ? undefined : reference.description,
                    updatedAt: props.publicMemoId ? undefined : reference.updatedAt,
                    website,
                }}
                placement={workspaceResourcePreviewPlacement(reference.resourceType, props.placement, 'rightTop')}
                publicMemoId={props.publicMemoId}
                zIndex={props.zIndex}
            >
                {trigger}
            </WorkspaceResourcePopover>
        );
    }

    const { activity } = props.item;
    const metadata = activity.metadata ?? {};
    const title =
        metadataString(metadata, ['targetName', 'resourceName', 'name']) ||
        activity.title ||
        resourceLabel(props.item.resourceType);
    const actor = activity.actor ?? activity.actorMember?.user;
    const actorName = [actor?.firstName, actor?.lastName].filter(Boolean).join(' ') || actor?.emailAddress || 'System';
    const parentName = metadataString(metadata, [
        'parentName',
        'dashboardName',
        'folderName',
        'investmentName',
        'listName',
        'pipelineName',
        'reportName',
    ]);
    const deletedAt =
        metadataDate(metadata, 'deletedAt') ||
        (activity.eventType === ResourceActivityEventType.DELETED ? activity.createdAt : undefined);
    const trigger = props.children ?? title;
    const listType = props.item.resourceType === 'lists' ? metadataString(metadata, ['listType', 'type']) : undefined;

    return (
        <WorkspaceResourcePopover
            data={{
                actorName,
                createdAt:
                    metadataDate(metadata, 'createdAt') ||
                    (activity.eventType === ResourceActivityEventType.CREATED ? activity.createdAt : undefined),
                deletedAt,
                eventLabel: getResourceActivityActionLabel(activity),
                href: props.item.url,
                id: props.item.resourceId,
                label: title,
                listType,
                parentName,
                resourceType: props.item.resourceType,
                status: props.item.status,
                summary: activity.summary ?? activity.action,
                updatedAt: activity.updatedAt ?? activity.createdAt,
                website,
            }}
            placement={workspaceResourcePreviewPlacement(props.item.resourceType, props.placement, 'rightTop')}
            zIndex={props.zIndex}
        >
            <span
                style={{
                    alignItems: 'center',
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadius,
                    display: 'inline-flex',
                    gap: '0.375rem',
                    lineHeight: 1,
                    maxWidth: '100%',
                    padding: '0.125rem 0.375rem 0.125rem 0.1875rem',
                    verticalAlign: '-0.275rem',
                }}
            >
                <WorkspaceResourceAvatar
                    listType={listType}
                    resource={props.item.resourceType}
                    size={20}
                    website={website}
                />
                <span
                    style={{
                        ...ellipsisStyling,
                        color: token.colorText,
                        display: 'inline-block',
                        fontWeight: 600,
                        lineHeight: '1.25rem',
                        maxWidth: '18rem',
                    }}
                >
                    {trigger}
                </span>
            </span>
        </WorkspaceResourcePopover>
    );
};

export default EnrichedWorkspaceResourcePopover;
